import { Link } from "react-router-dom";
import { usePlayer } from "@/store/PlayerContext";
import { Play, Pause, ShoppingBag } from "lucide-react";

// CSS gradient covers - cartoony, matches theme
const COVER_GRADIENTS = [
  "linear-gradient(135deg, #4CC9F0 0%, #FFD60A 100%)",
  "linear-gradient(135deg, #FFD60A 0%, #FF6FB5 100%)",
  "linear-gradient(135deg, #4CC9F0 0%, #0F1115 60%, #FFD60A 100%)",
  "linear-gradient(135deg, #FF6FB5 0%, #161A21 60%, #4CC9F0 100%)",
  "linear-gradient(45deg, #4CC9F0 0%, #0A0B0E 50%, #FF6FB5 100%)",
  "linear-gradient(135deg, #FFD60A 0%, #4CC9F0 100%)",
];

export function coverGradientFor(beat) {
  if (beat.cover_url) return null;
  const seed = (beat.id || "0").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return COVER_GRADIENTS[seed % COVER_GRADIENTS.length];
}

export default function BeatCard({ beat }) {
  const { play, current, playing } = usePlayer();
  const isCurrent = current?.id === beat.id;
  const isPlaying = isCurrent && playing;
  const gradient = coverGradientFor(beat);

  return (
    <article className="card-base overflow-hidden group fade-up" data-testid={`beat-card-${beat.id}`}>
      <div
        className="relative aspect-square overflow-hidden border-b-2 border-[var(--ink)]"
        style={gradient ? { background: gradient } : undefined}
      >
        {beat.cover_url && (
          <img
            src={beat.cover_url}
            alt={beat.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
        )}
        {!beat.cover_url && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-6xl font-bold text-[var(--ink)] opacity-70 select-none">
              {(beat.title || "?").charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <button
          onClick={() => play(beat)}
          className="absolute bottom-4 right-4 btn-primary w-12 h-12 rounded-full flex items-center justify-center"
          aria-label={isPlaying ? "Pause preview" : "Play preview"}
          data-testid={`play-beat-btn-${beat.id}`}
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        {beat.genre && (
          <span className="absolute top-3 left-3 tag-pill" data-testid={`beat-genre-${beat.id}`}>
            {beat.genre}
          </span>
        )}
        {beat.has_stems && (
          <span className="absolute top-3 right-3 tag-pill-pink" data-testid={`beat-stems-tag-${beat.id}`}>
            Stems
          </span>
        )}
      </div>
      <div className="p-5">
        <Link to={`/beat/${beat.id}`} className="block" data-testid={`beat-title-link-${beat.id}`}>
          <h3 className="font-display font-bold text-xl text-[var(--text)] hover:text-[var(--primary)] transition-colors truncate">
            {beat.title}
          </h3>
        </Link>
        <div className="flex items-center gap-2 mt-1 text-xs text-[var(--text-dim)] font-mono-cartoon">
          {beat.bpm ? <span>{beat.bpm} BPM</span> : null}
          {beat.bpm && beat.key_signature ? <span>·</span> : null}
          {beat.key_signature ? <span>{beat.key_signature}</span> : null}
        </div>
        <div className="flex items-end justify-between mt-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-dim)] font-mono-cartoon">From</div>
            <div className="font-display text-2xl font-bold text-[var(--secondary)]" data-testid={`beat-price-${beat.id}`}>
              ${beat.price_mp3?.toFixed(2)}
            </div>
          </div>
          <Link
            to={`/beat/${beat.id}`}
            className="btn-secondary px-4 py-2 text-sm flex items-center gap-1.5"
            data-testid={`beat-buy-link-${beat.id}`}
          >
            <ShoppingBag className="w-4 h-4" /> Buy
          </Link>
        </div>
      </div>
    </article>
  );
}
